'use client';
import type { UntypedFunction } from '@mnd-frontend/ts';
import { GREY_400, PURPLE_500, PURPLE_800, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import type React from 'react';
import { memo, useId, useState } from 'react';
import styled from 'styled-components';
import { PositionRelative, SvgChart } from '../lib/components';
import { Axes, Grid } from '../lib/components/Graph';
import BarChartTabs from '../lib/components/Tabs';
import { ContextProvider } from '../lib/context';
import { useScaleBandX, useScaleLinearY } from '../lib/hooks/d3';
import { max } from '../lib/utils';

export interface DataPoint {
  name: string;
  value: number;
  onClick?: () => void;
  subdued?: boolean;
  selected?: boolean;
}
interface PropsBar {
  data: DataPoint[];
  empty?: React.ReactNode;
  tabPanelId: string;
}

interface PropsBarChart extends Omit<PropsBar, 'tabPanelId'> {
  tabs: { value: string; label: string }[];
  selectedTab: string;
  onClickTab: UntypedFunction;
  height?: number;
}

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_4};
`;

const getChartTheme = (selected?: boolean, subdued?: boolean) => {
  if (subdued) {
    return GREY_400;
  }
  return selected ? PURPLE_800 : PURPLE_500;
};

const getChartThemeOnHover = (subdued?: boolean) => (subdued ? GREY_400 : PURPLE_800);

const Bars = memo(({ data, empty, tabPanelId }: PropsBar) => {
  const [hovered, setHovered] = useState('');
  const [focused, setFocused] = useState('');
  const maxValue = Math.ceil(max(data, d => d.value) || 1);

  const scaleX = useScaleBandX(data, 0.6, 0.2);
  const scaleY = useScaleLinearY(0, maxValue);

  const width = scaleX.bandwidth();

  const emptyStateX = scaleX(1);
  const emptyStateY = scaleY(0.6);

  return (
    <PositionRelative role="tabpanel" id={tabPanelId}>
      <SvgChart>
        <Grid scaleX={scaleX} scaleY={scaleY} />
        {data.filter(dataPoint => dataPoint.value > 0).length > 0
          ? data.map((d, i) => {
              const x = scaleX(i.toString()) || 0;
              const y = scaleY(d.value);
              const height = scaleY(0) - y;
              const isInteractive = Boolean(d.onClick && !d.subdued);
              const isToggle = typeof d.selected === 'boolean';
              const interactiveProps = isInteractive
                ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    onKeyDown: (event: React.KeyboardEvent) => {
                      if (event.code === 'Enter' || event.code === 'Space') {
                        event.stopPropagation();
                        event.preventDefault();
                        d.onClick?.();
                      }
                    },
                    onClick: d.onClick,
                    ...(isToggle ? ({ 'aria-pressed': d.selected } as const) : {}),
                  }
                : ({ role: 'img' } as const);
              return (
                /* biome-ignore lint/a11y/noStaticElementInteractions: Chart bars use mouse/focus events for hover/focus states */
                <rect
                  key={`${d.name}-${i}`}
                  y={y}
                  x={x}
                  rx="3"
                  width={width}
                  height={height}
                  cursor={d.onClick && !d.subdued ? 'pointer' : undefined}
                  {...interactiveProps}
                  aria-label={`${d.name}: ${Math.ceil((100 * height) / scaleY(0))}%`}
                  onFocus={event => {
                    event.stopPropagation();
                    setFocused(d.name);
                    setHovered('');
                  }}
                  onBlur={event => {
                    event.stopPropagation();
                    setFocused('');
                  }}
                  fill={
                    hovered === d.name || focused === d.name
                      ? getChartThemeOnHover(d.subdued)
                      : getChartTheme(d.selected, d.subdued)
                  }
                  onMouseEnter={event => {
                    event.stopPropagation();
                    setHovered(d.name);
                    setFocused('');
                  }}
                  onMouseLeave={event => {
                    event.stopPropagation();
                    setHovered('');
                    setFocused('');
                  }}
                />
              );
            })
          : empty && (
              <foreignObject y={emptyStateY} x={emptyStateX} width="90%" height="100%">
                {empty}
              </foreignObject>
            )}
        <Axes data={data} scaleX={scaleX} scaleY={scaleY} />
      </SvgChart>
    </PositionRelative>
  );
});

const BarChart = memo(
  ({ data, tabs, selectedTab, onClickTab, empty, height = 150 }: PropsBarChart) => {
    const tabPanelId = useId();

    return (
      <ContextProvider minWidth={200} height={height} selectToggle hideSelected>
        <ChartWrapper>
          <BarChartTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onClick={onClickTab}
            tabPanelId={tabPanelId}
          />
          <Bars data={data} empty={empty} tabPanelId={tabPanelId} />
        </ChartWrapper>
      </ContextProvider>
    );
  },
);

export default BarChart;
